import classNames from 'classnames'
import React, { MouseEventHandler, PropsWithChildren } from 'react'

import { useModals } from '@context/ModalsContext'

import { Title2 } from '@ui/Typography/Typography'
import { BaseComponentProps } from '@ui/common/component.props'
import { UnfoldMoreIcon } from '@ui/icons/UnfoldMore.icon'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'

import styles from './BaseInputButton.module.scss'

export type BaseInputButtonSize = 'lg' | 'xl'

export interface BaseInputButtonProps extends BaseComponentProps<HTMLButtonElement> {
  size?: BaseInputButtonSize
  startIcon?: IconFCType
  startElement?: React.ReactNode
  text?: string
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  dropdownContent?: React.ReactNode
  showDropdown?: boolean
  onToggleDropdown?: () => void
  onToggleModal?: () => void
  dropdownRef?: React.RefObject<HTMLDivElement | null>
}

const variantStyles: Record<BaseInputButtonSize, string> = {
  lg: styles['size-lg'],
  xl: styles['size-xl'],
} as const

type IconSize = 20 | 24

const getIconSize = (size?: BaseInputButtonSize): IconSize => {
  switch (size) {
    case 'xl':
      return 24
    default:
      return 20
  }
}

export const BaseInputButton: React.FC<PropsWithChildren<BaseInputButtonProps>> = (props) => {
  const {
    size = 'lg',
    startIcon: StartIcon,
    startElement: startIconElement,
    text,
    disabled = false,
    onClick,
    dropdownContent,
    showDropdown = false,
    onToggleDropdown,
    onToggleModal,
    dropdownRef,
    ...restProps
  } = props

  const isMobile = useWindowResize()
  const iconSize = getIconSize(size)
  const { modals } = useModals()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      if (onClick) {
        onClick(event)
      } else if (dropdownContent) {
        if (isMobile || modals.length > 0) {
          onToggleModal?.()
        } else {
          onToggleDropdown?.()
        }
      }
    }
  }

  return (
    <>
      <button
        type='button'
        className={classNames(styles.base, variantStyles[size], {
          [styles.stateDisabled]: disabled,
        })}
        disabled={disabled}
        onClick={handleClick}
        {...restProps}
      >
        {(StartIcon || startIconElement) && (
          <span className={styles.iconWrapper}>
            {StartIcon ? <StartIcon size={iconSize} /> : startIconElement}
          </span>
        )}

        {text && (
          <Title2 medium className={styles.text}>
            {text}
          </Title2>
        )}
        <span className={styles.iconWrapper}>
          <UnfoldMoreIcon size={iconSize} />
        </span>
      </button>

      {showDropdown && dropdownContent && (
        <div ref={dropdownRef} className={styles.dropdownContainer}>
          {dropdownContent}
        </div>
      )}
    </>
  )
}
